import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  formatNr2jDate,
  NR2J_TASK_TYPES,
  nr2jTaskStatusLabel,
  nr2jTaskTypeLabel,
  organizationDisplayName,
} from '../../utils/nr2jConstants';

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Nr2jTaskDrawer({
  task,
  busy = false,
  onClose,
  onSave,
  onComplete,
  onReopen,
  onCancel,
  onPlanFollowUp,
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('AUTRE');
  const [dueAt, setDueAt] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!task) return;
    setEditing(false);
    setTitle(task.title || '');
    setType(task.type || 'AUTRE');
    setDueAt(toDatetimeLocal(task.dueAt));
    setNotes(task.notes || '');
  }, [task]);

  if (!task) return null;

  const org = task.organization;
  const orgId = task.organizationId || org?.id;
  const isOpen = task.status === 'OPEN';
  const isDone = task.status === 'DONE';
  const isCancelled = task.status === 'CANCELLED';

  const persist = (patch) => onSave?.({
    title,
    type,
    dueAt: dueAt ? new Date(dueAt).toISOString() : null,
    notes: notes || null,
    ...patch,
  });

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="nr2j-task-drawer">
      <button type="button" className="absolute inset-0 bg-black/20" aria-label="Fermer" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Tâche</p>
            <h2 className="mt-0.5 truncate text-sm font-semibold text-gray-900">{task.title}</h2>
            <p className="mt-1 text-xs text-gray-500">
              {nr2jTaskTypeLabel(task.type)} · {nr2jTaskStatusLabel(task.status)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-500 hover:bg-gray-50">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 text-sm">
          {orgId ? (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2">
              <p className="text-xs text-indigo-700">Société liée</p>
              <Link
                to={`/nr2j/clients/${orgId}`}
                className="mt-0.5 inline-block font-medium text-indigo-900 underline"
                onClick={onClose}
              >
                {organizationDisplayName(org)} — Ouvrir la fiche société
              </Link>
            </div>
          ) : null}

          {!editing ? (
            <>
              <div>
                <p className="text-xs font-medium text-gray-500">Échéance</p>
                <p className="mt-0.5 text-gray-900">{formatNr2jDate(task.dueAt)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Notes</p>
                <p className="mt-0.5 whitespace-pre-wrap text-gray-800">
                  {task.notes?.trim() ? task.notes : 'Aucune note.'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
                <div>
                  <p className="font-medium">Créée</p>
                  <p>{formatNr2jDate(task.createdAt)}</p>
                </div>
                <div>
                  <p className="font-medium">Modifiée</p>
                  <p>{formatNr2jDate(task.updatedAt)}</p>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Titre</span>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Type</span>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  {NR2J_TASK_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Échéance</span>
                <input
                  type="datetime-local"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Notes</span>
                <textarea
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
            </div>
          )}
        </div>

        <footer className="space-y-2 border-t border-gray-100 px-4 py-3">
          {editing ? (
            <>
              <button
                type="button"
                disabled={busy || !title.trim()}
                onClick={async () => {
                  await persist();
                  setEditing(false);
                }}
                className="inline-flex w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {busy ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setEditing(false)}
                className="inline-flex w-full items-center justify-center rounded-xl border border-gray-300 px-4 py-2 text-sm"
              >
                Annuler l’édition
              </button>
            </>
          ) : (
            <>
              {isOpen ? (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEditing(true)}
                    className="inline-flex w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium"
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEditing(true)}
                    className="inline-flex w-full items-center justify-center rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900"
                  >
                    Reporter (éditer l’échéance)
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onComplete}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white"
                  >
                    Terminer
                  </button>
                  {typeof onCancel === 'function' ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={onCancel}
                      className="inline-flex w-full items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-600"
                    >
                      Annuler la tâche
                    </button>
                  ) : null}
                </>
              ) : null}
              {isDone || isCancelled ? (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onReopen}
                    className="inline-flex w-full items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-800"
                  >
                    Remettre à faire
                  </button>
                  {isDone && orgId && typeof onPlanFollowUp === 'function' ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={onPlanFollowUp}
                      className="inline-flex w-full items-center justify-center rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900"
                    >
                      Planifier une nouvelle relance
                    </button>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </footer>
      </aside>
    </div>
  );
}
